import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { ModalsService } from '@core/services/modals/modals.service';
import { ProductService } from '@core/services/product/product.service';
import { TranslateModule } from '@ngx-translate/core';
import { staggeredListAnimation } from '@shared/animations/list.animation';
import {
  DraftAdditionProductModel,
  DraftAdditionsGroup,
} from '@shared/local-store/cart/util/cart-simple-product-util';
import { KioskProductCardAtomComponent } from '@shared/atoms/kiosk/kiosk-product-cards/kiosk-product-card-atom/kiosk-product-card-atom.component';
import { KioskSelectionGroupWrapperComponent } from '@shared/blocks/kiosk/kiosk-selection-group-wrapper-atom/kiosk-selection-group-wrapper-atom.component';
import { KioskProductOverviewModalConfig } from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { CallComponentMethodPipe } from '@shared/pipes/call-component-method/call-component-method.pipe';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';

@Component({
  selector: 'app-kiosk-select-additions-product-organism',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ContentBuilderColorPipe,
    CallComponentMethodPipe,
    KioskSelectionGroupWrapperComponent,
    KioskProductCardAtomComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [staggeredListAnimation],
  template: `
    <app-kiosk-selection-group-wrapper-atom [secondaryColor]="secondaryColor()">
      <div
        class="grid grid-cols-3 gap-10"
        [@staggeredListAnimation]="{
          value: simpleProductAdditionsGroup().items.length,
          params: { animateEnterElementFromTopToBottom: true },
        }"
      >
        @for (addition of simpleProductAdditionsGroup().items; track trackById($index, addition)) {
          @if (addition && !addition.hidden) {
            <div
              class="rounded-2xl"
              [ngClass]="{
                'outline outline-[8px]': (addition.quantity ?? 0) > 0,
              }"
              [style.outlineColor]="buttonBackgroundColor() | contentBuilderColor"
            >
              <app-kiosk-product-card-atom
                [class]="optionCardType()"
                [product]="addition"
                [quantity]="addition.quantity"
                (changeProductQuantity)="
                  onChangeAdditionQuantity.emit({ additionId: addition.id, quantity: $event })
                "
                [isProductAvailable]="isAvailable(addition)"
                [selectionnable]="isAvailable(addition)"
                [displayPrice]="false"
                [displayedQuantity]="addition.quantity ?? 0"
                [textColor]="textColor()"
                [cardColor]="cardColor()"
                [buttonBackgroundColor]="buttonBackgroundColor()"
                [buttonTextColor]="buttonTextColor()"
                [buttonBackgroundColor]="primaryButtonBackgroundColor()"
                [unavailableButtonBackground]="unavailableButtonBackground()"
                [unavailableButtonText]="unavailableButtonText()"
                [secondaryColor]="secondaryColor()"
                [maxProduct]="addition.remainingAuthorizedQuantity"
                [monoSelection]="simpleProductAdditionsGroup().max_count === 1"
                [displayedAddition]="addition | appFn: getDisplayedAdditionPrice"
                (showProduct)="openProductModal(addition)"
              ></app-kiosk-product-card-atom>
            </div>
          }
        }
        @if (!simpleProductAdditionsGroup().items?.length) {
          <div class="py-4">
            {{ 'zerosix.offered-product-modal.no-product-available' | translate }}
          </div>
        }
      </div>
    </app-kiosk-selection-group-wrapper-atom>
  `,
})
export class KioskSelectAdditionsProductOrganismComponent {
  private readonly modalsService: ModalsService<KioskProductOverviewModalConfig> =
    inject(ModalsService);

  private readonly productService = inject(ProductService);

  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  // defaultCard = pas de classe spécifique
  protected readonly optionCardType = computed(() => {
    const type = this.kioskGlobalSettingsService.settings().productCard.styles.type;
    return type === 'defaultCard' ? '' : type;
  });

  public readonly backgroundColor = input<IContentBuilderFieldColor>();
  public readonly secondaryColor = input<IContentBuilderFieldColor>();
  public readonly cardColor = input<IContentBuilderFieldColor>();
  public readonly textColor = input<IContentBuilderFieldColor>();
  public readonly primaryButtonBackgroundColor = input<IContentBuilderFieldColor>();
  public readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  public readonly buttonTextColor = input<IContentBuilderFieldColor>();
  public readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  public readonly unavailableButtonText = input<IContentBuilderFieldColor>();

  public readonly simpleProductAdditionsGroup = input.required<DraftAdditionsGroup>();

  public readonly onChangeAdditionQuantity = output<{ additionId: string; quantity: number }>();

  protected isAvailable(addition: DoodProductModel): boolean {
    return this.productService.isAvailable(addition);
  }

  protected getDisplayedAdditionPrice = (addition: DraftAdditionProductModel): number => {
    return addition.uiPrice ? addition.uiPrice * 100 : 0;
  };

  protected trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  openProductModal(product: DoodProductModel): void {
    this.modalsService.open('kiosk-add-product-overview-modal', undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      type: product.type,
      colors: {
        background: this.backgroundColor(),
        accent: this.secondaryColor(),
      },
    });
  }
}
